import type {
	ContactEntry,
	DeviceSession,
	NonContact,
	NonContactsPage,
	PrivacySettings,
	PrivacyUpdate,
	ProfileUpdate,
	User
} from '@andresot/protocol';
import type { AccountUser } from './auth.js';
import { request } from './client.js';

/** Запись книжки; `mutual` — он тоже добавил меня («друг»). */
export type ContactWithUser = ContactEntry;
export type { NonContact };

export async function updateProfile(input: ProfileUpdate): Promise<AccountUser> {
	const result = await request<{ user: AccountUser }>('/api/profile', {
		method: 'PATCH',
		body: input
	});
	return result.user;
}

/** Оформление подписчика: эмодзи-статус и цвет профиля. `null` — убрать. */
export async function updatePremiumLook(input: {
	emojiStatus?: string | null;
	profileAccent?: number | null;
}): Promise<AccountUser> {
	const result = await request<{ user: AccountUser }>('/api/profile/premium-look', {
		method: 'PATCH',
		body: input
	});
	return result.user;
}

/** Что нужно знать до смены логина: цена и не рано ли. */
export interface LoginTerms {
	priceStars: number;
	/** Фактический баланс из той же базы, где пройдёт списание. */
	balance: number;
	/** Когда снова можно менять. Пусто — можно прямо сейчас. */
	availableAt?: number;
	/** Смена положена бесплатно — раз в год с ANDRESOT Premium. */
	free?: boolean;
	/** С какой длины логин доступен этому человеку: без подписки — с пяти символов. */
	minLength: number;
}

export function fetchLoginTerms(): Promise<LoginTerms> {
	return request<LoginTerms>('/api/account/login/terms');
}

export async function loginAvailable(login: string): Promise<boolean> {
	const result = await request<{ available: boolean }>(
		`/api/account/login/available?login=${encodeURIComponent(login)}`
	);
	return result.available;
}

export interface LoginChanged {
	login: string;
	mailbox: string | null;
	balance: number;
	/** Токен с уже актуальным login claim для REST и следующего WS reconnect. */
	accessToken: string;
}

export function changeLogin(login: string): Promise<LoginChanged> {
	return request<LoginChanged>('/api/account/login', { method: 'POST', body: { login } });
}

export async function fetchPrivacy(): Promise<PrivacySettings> {
	const result = await request<{ privacy: PrivacySettings }>('/api/privacy');
	return result.privacy;
}

export async function updatePrivacy(input: PrivacyUpdate): Promise<PrivacySettings> {
	const result = await request<{ privacy: PrivacySettings }>('/api/privacy', {
		method: 'PATCH',
		body: input
	});
	return result.privacy;
}

export async function fetchBlockedUsers(): Promise<User[]> {
	const result = await request<{ users: User[] }>('/api/privacy/blocked');
	return result.users;
}

export async function blockUser(userId: string): Promise<void> {
	await request('/api/privacy/blocked', { body: { userId } });
}

export async function unblockUser(userId: string): Promise<void> {
	await request(`/api/privacy/blocked/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

/** Сменить пароль. Возвращает, сколько прочих устройств при этом отключено. */
export async function changePassword(
	currentPassword: string,
	newPassword: string,
	code?: string
): Promise<number> {
	const result = await request<{ revoked: number }>('/api/account/password', {
		body: { currentPassword, newPassword, code }
	});
	return result.revoked;
}

export async function fetchSessions(): Promise<DeviceSession[]> {
	const result = await request<{ sessions: DeviceSession[] }>('/api/sessions');
	return result.sessions;
}

export async function revokeSession(familyId: string): Promise<void> {
	await request(`/api/sessions/${encodeURIComponent(familyId)}`, { method: 'DELETE' });
}

export async function revokeOtherSessions(): Promise<number> {
	const result = await request<{ revoked: number }>('/api/sessions', { method: 'DELETE' });
	return result.revoked;
}

export async function fetchContacts(): Promise<ContactWithUser[]> {
	const result = await request<{ contacts: ContactWithUser[] }>('/api/contacts');
	return result.contacts;
}

/**
 * Не в контактах: сначала связанные (добавили меня, переписка, общие группы), потом все
 * остальные пользователи по имени — страницами. `q` ищет по логину и имени на сервере.
 */
export async function fetchNonContacts(
	input: { cursor?: string; q?: string } = {}
): Promise<NonContactsPage> {
	const params = new URLSearchParams();
	if (input.cursor) params.set('cursor', input.cursor);
	if (input.q) params.set('q', input.q);
	const query = params.toString();
	return request<NonContactsPage>(`/api/contacts/others${query ? `?${query}` : ''}`);
}

export async function renameContact(
	userId: string,
	input: { alias: string; aliasLastName: string }
): Promise<ContactWithUser> {
	const result = await request<{ contact: ContactWithUser }>(
		`/api/contacts/${encodeURIComponent(userId)}`,
		{ method: 'PATCH', body: input }
	);
	return result.contact;
}

export async function searchUsers(query: string): Promise<User[]> {
	const params = new URLSearchParams({ q: query });
	const result = await request<{ users: User[] }>(`/api/contacts/search?${params}`);
	return result.users;
}

export async function addContact(userId: string): Promise<void> {
	await request('/api/contacts', { body: { userId } });
}

export async function removeContact(userId: string): Promise<void> {
	await request(`/api/contacts/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function createDialog(userId: string): Promise<string> {
	const result = await request<{ chatId: string }>('/api/chats/dialog', { body: { userId } });
	return result.chatId;
}
